import { capitalize, isNil, every, cloneDeep } from 'lodash-es';
import { dateUtils, appLocale } from 'shared';

import type { bloomreachEventsConfig } from '@/shared/lib';

import { convertToBloomreachFormat } from '../../bloomreach-events/convert-to-bloomreach-format';
import { useBloomreachEventsState } from '../model';
import { collectProperties } from '../lib';
import type {
  ExponeaAPI,
  CustomerIDs,
  CustomerProperties,
  EventProperties,
  MappedCustomerProperties,
  MappedCustomerIDs,
} from '../types';
import { bloomreachDebug } from './debug';

declare global {
  const exponea: ExponeaAPI;
}

export const anonymize = () => {
  bloomreachDebug('anonymize');
  exponea.anonymize();
};

const mapCustomerProperties = (
  props: CustomerProperties,
): MappedCustomerProperties => {
  return {
    email: props?.email,
    birth_date: props.birthDate ? dateUtils.toUnix(props.birthDate) : undefined,
    first_name: capitalize(props.firstName),
    is_freedom_client: true, // Since we are in Freedom Superapp, it is true by default
  };
};

let userId: string | undefined;
let emailId: string | undefined;

const shouldUpdateIds = (ids: CustomerIDs) => {
  return ids.userId !== userId || ids.email !== emailId;
};

export const identify = (ids: CustomerIDs, props?: CustomerProperties) => {
  if (!shouldUpdateIds(ids) && every(props, isNil)) {
    return;
  }

  const mappedIds: MappedCustomerIDs = {
    user_id: ids.userId,
  };
  if (ids.email) {
    mappedIds.email_id = ids.email;
    emailId = ids.email;
  }
  userId = ids.userId;

  let mappedProps: MappedCustomerProperties = {
    is_freedom_client: true,
  };
  if (props) {
    mappedProps = mapCustomerProperties(props);
  }

  bloomreachDebug('identify', mappedIds, mappedProps);
  exponea.identify(mappedIds, mappedProps);
};

export const track = (
  event: bloomreachEventsConfig.BloomreachEventName,
  properties?: EventProperties,
  options?: { skipCollectProperties?: boolean },
) => {
  const bloomreachEventsState = useBloomreachEventsState();
  // Save original properties to storage (ISO datetime strings for accelera to convert)
  bloomreachEventsState.updateState({ event, properties: properties || {} });

  const locale = appLocale.lib.getAppLocale();

  const collectedProperties = options?.skipCollectProperties
    ? {}
    : collectProperties(event);
  const finalProperties = {
    ...collectedProperties,
    ...(properties || {}),
    language: locale,
  };

  // Convert ISO datetime strings to Unix before sending to exponea
  const propertiesForExponea = cloneDeep(finalProperties);
  convertToBloomreachFormat(propertiesForExponea);

  bloomreachDebug('track', event, propertiesForExponea);
  exponea.track(event, propertiesForExponea);
};

export const update = (props: CustomerProperties) => {
  const mappedProps = mapCustomerProperties(props);
  bloomreachDebug('update', mappedProps);
  exponea.update(mappedProps);
};

export const resetEventsState = () => {
  const bloomreachEventsState = useBloomreachEventsState();
  bloomreachEventsState.resetState();
  bloomreachDebug('resetEventsState');
};
